import type { NotificationService } from './notification.interface.ts'
import type {
  EmailPayload,
  SMSPayload,
  WhatsAppPayload,
  TelegramPayload,
  PushPayload,
  NotificationResult,
  NotificationRecord,
  NotificationHistoryFilter,
} from './notification.types.ts'
import { NotificationChannel } from './notification.types.ts'
import { getSupabaseClient } from '../database/supabase.client.ts'
import {
  registerPushServiceWorker,
  showLocalNotification,
} from './push.service.ts'
import { env } from '@/config/env.ts'

const NOTIFICATIONS_TABLE = 'notifications'

/**
 * POST to same-origin Vercel Serverless Functions at `/api/notifications/*`.
 * In Vite dev, these are proxied via vite.config.ts to the Vercel dev server
 * or can be pointed elsewhere via VITE_NOTIFICATION_API_BASE.
 */
async function postNotificationApi<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<{ data: T | null; error: Error | null }> {
  const base =
    (typeof env.notificationApiBase === 'string' &&
      env.notificationApiBase.trim()) ||
    ''
  const url = `${base}/api/notifications/${path}`

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    const text = await res.text()
    let parsed: unknown = null
    if (text) {
      try {
        parsed = JSON.parse(text) as unknown
      } catch {
        parsed = { error: text }
      }
    }

    if (!res.ok) {
      const obj =
        parsed && typeof parsed === 'object'
          ? (parsed as Record<string, unknown>)
          : null
      const errMsg =
        (typeof obj?.error === 'string' && obj.error) ||
        (typeof obj?.message === 'string' && obj.message) ||
        `Notification API "${path}" failed (${res.status})`
      return { data: null, error: new Error(errMsg) }
    }

    return { data: (parsed ?? {}) as T, error: null }
  } catch (e) {
    return {
      data: null,
      error: new Error(
        `Notification API "${path}" request failed: ${e instanceof Error ? e.message : String(e)}`,
      ),
    }
  }
}

function isPushConfigured(): boolean {
  return Boolean(env.vapidPublicKey)
}

async function logNotification(
  userAddress: string,
  channel: string,
  recipient: string,
  body: string,
  status: 'sent' | 'failed',
  subject?: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  try {
    const supabase = getSupabaseClient()
    await supabase.from(NOTIFICATIONS_TABLE).insert({
      user_address: userAddress,
      channel,
      recipient,
      subject,
      body,
      status,
      metadata: metadata ?? {},
    })
  } catch {
    // Logging failure should not break the notification flow
  }
}

export function createNotificationService(): NotificationService {
  return {
    async sendEmail(payload: EmailPayload): Promise<NotificationResult> {
      try {
        const { data, error } = await postNotificationApi<
          Record<string, unknown>
        >('email', {
          to: payload.to,
          subject: payload.subject,
          body: payload.body,
          html: payload.html,
        })

        if (error) {
          await logNotification(
            '',
            NotificationChannel.Email,
            payload.to,
            payload.body,
            'failed',
            payload.subject,
            { error: error.message },
          )
          return {
            success: false,
            channel: NotificationChannel.Email,
            error: error.message,
          }
        }

        const messageId = (data as Record<string, unknown>)?.id as
          | string
          | undefined

        await logNotification(
          '',
          NotificationChannel.Email,
          payload.to,
          payload.body,
          'sent',
          payload.subject,
          {
            ...(payload.metadata ?? {}),
            messageId,
          },
        )

        return {
          success: true,
          channel: NotificationChannel.Email,
          messageId,
        }
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Email send failed'
        return {
          success: false,
          channel: NotificationChannel.Email,
          error: errorMsg,
        }
      }
    },

    async sendSMS(payload: SMSPayload): Promise<NotificationResult> {
      try {
        const { data, error } = await postNotificationApi<
          Record<string, unknown>
        >('sms', {
          to: payload.to,
          message: payload.message,
        })

        if (error) {
          await logNotification(
            '',
            NotificationChannel.SMS,
            payload.to,
            payload.message,
            'failed',
            undefined,
            { error: error.message },
          )
          return {
            success: false,
            channel: NotificationChannel.SMS,
            error: error.message,
          }
        }

        const messageId = (data as Record<string, unknown>)?.sid as
          | string
          | undefined

        await logNotification(
          '',
          NotificationChannel.SMS,
          payload.to,
          payload.message,
          'sent',
          undefined,
          { messageId },
        )

        return {
          success: true,
          channel: NotificationChannel.SMS,
          messageId,
        }
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'SMS send failed'
        return {
          success: false,
          channel: NotificationChannel.SMS,
          error: errorMsg,
        }
      }
    },

    async sendWhatsApp(payload: WhatsAppPayload): Promise<NotificationResult> {
      try {
        const { data, error } = await postNotificationApi<
          Record<string, unknown>
        >('whatsapp', {
          to: payload.to,
          message: payload.message,
          from: payload.from,
        })

        if (error) {
          await logNotification(
            '',
            NotificationChannel.WhatsApp,
            payload.to,
            payload.message,
            'failed',
            undefined,
            { error: error.message },
          )
          return {
            success: false,
            channel: NotificationChannel.WhatsApp,
            error: error.message,
          }
        }

        const messageId = (data as Record<string, unknown>)?.sid as
          | string
          | undefined

        await logNotification(
          '',
          NotificationChannel.WhatsApp,
          payload.to,
          payload.message,
          'sent',
          undefined,
          { messageId },
        )

        return {
          success: true,
          channel: NotificationChannel.WhatsApp,
          messageId,
        }
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'WhatsApp send failed'
        return {
          success: false,
          channel: NotificationChannel.WhatsApp,
          error: errorMsg,
        }
      }
    },

    async sendTelegram(payload: TelegramPayload): Promise<NotificationResult> {
      try {
        const { data, error } = await postNotificationApi<
          Record<string, unknown>
        >('telegram', {
          chatId: payload.chatId,
          message: payload.message,
          parseMode: payload.parseMode,
        })

        if (error) {
          await logNotification(
            '',
            NotificationChannel.Telegram,
            payload.chatId,
            payload.message,
            'failed',
            undefined,
            { error: error.message },
          )
          return {
            success: false,
            channel: NotificationChannel.Telegram,
            error: error.message,
          }
        }

        const rawMid = (data as Record<string, unknown>)?.message_id
        const messageId =
          rawMid !== undefined && rawMid !== null ? String(rawMid) : undefined

        await logNotification(
          '',
          NotificationChannel.Telegram,
          payload.chatId,
          payload.message,
          'sent',
          undefined,
          { messageId },
        )

        return {
          success: true,
          channel: NotificationChannel.Telegram,
          messageId,
        }
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Telegram send failed'
        return {
          success: false,
          channel: NotificationChannel.Telegram,
          error: errorMsg,
        }
      }
    },

    async sendPush(payload: PushPayload): Promise<NotificationResult> {
      try {
        if (!isPushConfigured()) {
          return {
            success: false,
            channel: NotificationChannel.Push,
            error: 'Push notifications not configured (VAPID key missing)',
          }
        }

        if (!this.isPushSupported()) {
          return {
            success: false,
            channel: NotificationChannel.Push,
            error: 'Push notifications are not supported in this browser',
          }
        }

        if (!this.isPushPermitted()) {
          return {
            success: false,
            channel: NotificationChannel.Push,
            error: 'Push notification permission not granted',
          }
        }

        await registerPushServiceWorker()
        await showLocalNotification(payload.title, {
          body: payload.body,
          icon: payload.icon ?? '/bitslegacy_logo.png',
          tag: payload.tag,
          data: { url: payload.url },
        })

        await logNotification(
          '',
          NotificationChannel.Push,
          'browser',
          payload.body,
          'sent',
          payload.title,
        )

        return {
          success: true,
          channel: NotificationChannel.Push,
        }
      } catch (e) {
        const errorMsg =
          e instanceof Error ? e.message : 'Push notification failed'
        return {
          success: false,
          channel: NotificationChannel.Push,
          error: errorMsg,
        }
      }
    },

    async requestPushPermission(): Promise<NotificationPermission> {
      if (!isPushConfigured() || !this.isPushSupported()) return 'denied'
      return Notification.requestPermission()
    },

    isPushSupported(): boolean {
      return (
        isPushConfigured() &&
        typeof window !== 'undefined' &&
        'Notification' in window &&
        'serviceWorker' in navigator
      )
    },

    isPushPermitted(): boolean {
      if (!this.isPushSupported()) return false
      return Notification.permission === 'granted'
    },

    async getHistory(
      userAddress: string,
      filters?: NotificationHistoryFilter,
    ): Promise<NotificationRecord[]> {
      try {
        const supabase = getSupabaseClient()
        let query = supabase
          .from(NOTIFICATIONS_TABLE)
          .select('*')
          .eq('user_address', userAddress)
          .order('created_at', { ascending: false })

        if (filters?.channel) {
          query = query.eq('channel', filters.channel)
        }
        if (filters?.status) {
          query = query.eq('status', filters.status)
        }
        if (filters?.limit) {
          query = query.limit(filters.limit)
        }
        if (filters?.offset) {
          query = query.range(
            filters.offset,
            filters.offset + (filters.limit ?? 50) - 1,
          )
        }

        const { data, error } = await query

        if (error) return []

        return (
          (data as Record<string, unknown>[])?.map(
            (row) =>
              ({
                id: row.id as string,
                userAddress: row.user_address as string,
                channel: row.channel as NotificationRecord['channel'],
                recipient: row.recipient as string,
                subject: row.subject as string | undefined,
                body: row.body as string,
                status: row.status as 'sent' | 'failed',
                metadata: row.metadata as Record<string, unknown> | undefined,
                createdAt: row.created_at as string,
              }) satisfies NotificationRecord,
          ) ?? []
        )
      } catch {
        return []
      }
    },
  }
}
